import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../constants';

const CharacterStats = ({ characterId, initialStats = [], onSave, isNew = false, selectedCostume }) => {
    const { t } = useTranslation();
    const [startingStar, setStartingStar] = useState(1);
    const [statsData, setStatsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Initialize stats data when initialStats or selectedCostume changes
    useEffect(() => {
        if (initialStats && initialStats.length > 0) {
            // Map existing stats to state
            const formattedStats = initialStats.map(stat => ({
                Estrellas: stat.Estrellas,
                Speed: stat.Speed || 0,
                Stamina: stat.Stamina || 0,
                Power: stat.Power || 0,
                Guts: stat.Guts || 0,
                Wit: stat.Wit || 0
            }));
            setStatsData(formattedStats);

            // Determine starting star based on lowest star present
            const minStar = Math.min(...formattedStats.map(s => s.Estrellas));
            setStartingStar(minStar);
        } else {
            // Initialize with default rows if no stats found
            // Default to 1 star or costume's initial stars if available
            const defaultStart = selectedCostume?.stars_initial || 1;
            setStartingStar(defaultStart);

            // Generate default rows directly to avoid dependency on updateStatsRows
            const newStats = [];
            for (let i = defaultStart; i <= 5; i++) {
                newStats.push({
                    Estrellas: i,
                    Speed: 0,
                    Stamina: 0,
                    Power: 0,
                    Guts: 0,
                    Wit: 0
                });
            }
            setStatsData(newStats);
        }
    }, [initialStats, selectedCostume]);

    const updateStatsRows = (startStar, currentData = statsData) => {
        const newStats = [];
        for (let i = startStar; i <= 5; i++) {
            // Try to find existing data for this star level
            const existing = currentData.find(s => s.Estrellas === i);
            newStats.push(existing || {
                Estrellas: i,
                Speed: 0,
                Stamina: 0,
                Power: 0,
                Guts: 0,
                Wit: 0
            });
        }
        setStatsData(newStats);
    };

    const handleStarChange = (e) => {
        const newStart = parseInt(e.target.value);
        setStartingStar(newStart);
        updateStatsRows(newStart);
    };

    const handleStatChange = (star, field, value) => {
        setStatsData(prev => prev.map(item =>
            item.Estrellas === star
                ? { ...item, [field]: parseInt(value) || 0 }
                : item
        ));
        if (success) setSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedCostume?.card_id) {
            setError(t('CharacterStats.error_select_costume'));
            return;
        }

        // If new character, pass data up
        if (isNew) {
            onSave(statsData);
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // Loop of PUT requests for existing stats
            // Now including card_id in the body just in case, though endpoint might use URL params
            // The endpoint is PUT /stats/<int:id_personaje>/<int:estrellas>
            // But wait, stats are now unique by (card_id, Id_Personaje, Estrellas).
            // The previous endpoint might update ALL stats for that char/star?
            // I need to check if the backend supports updating by card_id.
            // If not, I might need to update the API or use POST to overwrite.

            // Assuming I need to use POST to ensure card_id is set correctly or a new endpoint.
            // Let's check api.py later. For now, I'll assume I can use POST /stats to upsert.
            // Or I should use the PUT endpoint but it might be ambiguous without card_id.

            // Actually, the user said "stats van a depender del traje".
            // The table has card_id.
            // I should probably use a POST to /stats which handles upsert based on keys.
            // Or if I use PUT, I need to make sure it targets the specific row.

            // Let's try using POST /stats with the list, assuming the backend handles upsert or I'll fix it.
            // The backend POST /stats takes { Id_Personaje, stats: [...] } usually.
            // I should check if it accepts card_id.

            // Given the previous logic:
            /*
            const statsPayload = {
                Id_Personaje: newId,
                card_id: newCardId,
                stats: newCharData.stats
            };
            */
            // This suggests POST supports it.

            const payload = {
                Id_Personaje: characterId,
                card_id: selectedCostume.card_id,
                stats: statsData
            };

            const response = await fetch(`${API_BASE_URL}/stats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || t('CharacterStats.error_save'));
            }

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);

            // Refresh parent
            onSave();

        } catch (err) {
            setError(t('CharacterStats.error_save') + ': ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!selectedCostume) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 opacity-50">
                <h3 className="text-lg font-bold text-gray-800 mb-2">{t('CharacterStats.title_empty')}</h3>
                <p className="text-gray-500">{t('CharacterStats.msg_empty')}</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h3 className="text-lg font-bold text-gray-800">{t('CharacterStats.title_stats', { title: selectedCostume.title_en })}</h3>
                    <p className="text-xs text-gray-500">{t('CharacterStats.label_card_id', { id: selectedCostume.card_id })}</p>
                </div>

                <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">{t('CharacterStats.label_initial_stars')}</label>
                    <select
                        value={startingStar}
                        onChange={handleStarChange}
                        className="p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-green outline-none"
                    >
                        {[1, 2, 3, 4, 5].map(num => (
                            <option key={num} value={num}>{num} ★</option>
                        ))}
                    </select>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-700 font-medium">
                            <tr>
                                <th className="px-4 py-3 rounded-tl-lg">{t('CharacterStats.header_stars')}</th>
                                <th className="px-4 py-3 text-center">Speed</th>
                                <th className="px-4 py-3 text-center">Stamina</th>
                                <th className="px-4 py-3 text-center">Power</th>
                                <th className="px-4 py-3 text-center">Guts</th>
                                <th className="px-4 py-3 text-center rounded-tr-lg">Wit</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {statsData.map((row) => (
                                <tr key={row.Estrellas} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-yellow-600">
                                        {row.Estrellas} ★
                                    </td>
                                    {['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].map(stat => (
                                        <td key={stat} className="px-2 py-2">
                                            <input
                                                type="number"
                                                value={row[stat]}
                                                onChange={(e) => handleStatChange(row.Estrellas, stat, e.target.value)}
                                                className="w-full p-2 text-center border border-gray-300 rounded focus:ring-2 focus:ring-primary-green focus:border-transparent outline-none"
                                                min="0"
                                                max="2000"
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {error && (
                    <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                        {t('CharacterStats.success_msg')}
                    </div>
                )}

                {!isNew && (
                    <div className="flex justify-end pt-4 mt-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 bg-primary-green text-white rounded-lg font-medium hover:bg-green-600 transition-colors ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? t('CharacterStats.button_saving') : t('CharacterStats.button_save')}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default CharacterStats;
// Revisado: 2025-12-16
